import { API_URL } from './config';

export interface Message {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

export async function* streamChat(
  messages: Message[],
  onToken: (token: string) => void,
  signal?: AbortSignal
): AsyncGenerator<string> {
  const token = localStorage.getItem('coderai_token');

  const response = await fetch(`${API_URL}/chat_proxy.php`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    signal,
    body: JSON.stringify({
      messages
    })
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`Server error ${response.status}: ${text}`);
  }

  const reader = response.body!.getReader();
  const decoder = new TextDecoder();

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    const lines = decoder.decode(value).split('\n').filter(Boolean);
    for (const line of lines) {
      try {
        const data = JSON.parse(line);
        if (data.message?.content) {
          onToken(data.message.content);
          yield data.message.content;
        }
      } catch {}
    }
  }
}

// ─── Phase 1: file upload ──────────────────────────────────────────────────

export interface UploadedFileResult {
  ok: boolean;
  filename: string;
  type: 'pdf' | 'docx' | 'image';
  content: string;
}

export async function uploadFile(
  file: File,
  token: string | null
): Promise<UploadedFileResult> {
  const form = new FormData();
  form.append('file', file);

  const response = await fetch(`${API_URL}/upload_file.php`, {
    method: 'POST',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: form
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.error || `Server error ${response.status}`);
  }

  return data;
}

// ─── Phase 4: generate a real .pptx / .pdf from a structured spec ─────────

export interface GenerateFilePayload {
  type: 'pptx' | 'pdf' | 'docx' | 'xlsx';
  content: string; // raw JSON string from the ```pptx-json / ```pdf-json fence
}

export async function generateFile(
  payload: GenerateFilePayload,
  token: string | null
): Promise<Blob> {
  const response = await fetch(`${API_URL}/generate_file.php`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || `Server error ${response.status}`);
  }

  return response.blob();
}

// ─── Real-time web search grounding ────────────────────────────────────────

export interface WebSearchResult {
  title: string;
  snippet: string;
  link: string;
}

export interface WebSearchResponse {
  ok: boolean;
  query: string;
  results: WebSearchResult[];
}

export async function webSearch(
  query: string,
  token: string | null
): Promise<WebSearchResponse> {
  const response = await fetch(`${API_URL}/web_search.php`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ query })
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.error || `Server error ${response.status}`);
  }

  return data;
}